import { useEffect, useState, useCallback, useRef } from 'react';
import { useAuth } from './useAuth';
import { notificationsApi } from '../lib/notificationsApi';

const POLL_MS = 60000;

export function useNotifications() {
  const { account } = useAuth();
  const accountId = account?.id;
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const timer = useRef(null);

  const refresh = useCallback(async () => {
    if (!accountId) return;
    try {
      const d = await notificationsApi.list();
      setNotifications(d.notifications || []);
      setUnreadCount(d.unreadCount || 0);
    } catch { /* offline / signed-out — keep cached */ }
  }, [accountId]);

  const markRead = useCallback(async (id) => {
    setNotifications((ns) => ns.map((n) => (n.id === id && !n.read_at ? { ...n, read_at: new Date().toISOString() } : n)));
    setUnreadCount((c) => Math.max(0, c - 1));
    try { await notificationsApi.markRead(id); } catch { /* will reconcile on next poll */ }
  }, []);

  const markAllRead = useCallback(async () => {
    setNotifications((ns) => ns.map((n) => (n.read_at ? n : { ...n, read_at: new Date().toISOString() })));
    setUnreadCount(0);
    try { await notificationsApi.markAllRead(); } catch { /* */ }
  }, []);

  useEffect(() => {
    if (!accountId) return;
    refresh();
    timer.current = setInterval(refresh, POLL_MS);
    const onFocus = () => refresh();
    window.addEventListener('focus', onFocus);
    return () => { clearInterval(timer.current); window.removeEventListener('focus', onFocus); };
  }, [accountId, refresh]);

  return { notifications, unreadCount, refresh, markRead, markAllRead };
}
